// v6 F9.2 / F9.6 — Trap-zone properties editor.
// Toont 7 trap-specifieke invoervelden voor rooms met room_type='stair'.
// Vervangt L/B/H + meetmethode + openingen & aftrekposten voor dat type ruimte.
//
// F9.6 Mobile UX polish:
// - Donkerdere labels (text-[#2C2C2A]) voor sterk contrast op de werf.
// - Grotere/dikkere invoerwaarden (h-12 + text-lg font-bold) zodat 13/4/2/20
//   in één oogopslag leesbaar zijn.
// - Onder 480px: alle velden onder elkaar (grid-cols-1). 480-639px: 2 kolommen.
//   ≥640px: 3 kolommen.
// - Tip-tekst vervangen door Info-icoon + Popover (tap-friendly).

import React from "react";
import { Label } from "../ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Footprints, Info } from "lucide-react";
import RawNumInput from "./RawNumInput";

const DEFAULT_PROPS = {
  stair_type: "closed",
  tread_count: 0,
  handrail_lm: 0,
  balustrade_lm: 0,
  spindle_count: 0,
  landing_m2: 0,
  underside_m2: 0,
};

const STAIR_TYPES = [
  { key: "open",          label: "Open trap" },
  { key: "closed",        label: "Gesloten trap" },
  { key: "with_landing",  label: "Trap met overloop" },
];

export default function StairPropsBlock({ room, onUpdateRoom }) {
  const sp = { ...DEFAULT_PROPS, ...(room?.stair_props || {}) };
  const patch = (delta) => onUpdateRoom({ stair_props: { ...sp, ...delta } });

  return (
    <div
      className="border border-[#E5E2DC] rounded-xl bg-[#FAF9F6] p-3 sm:p-4 space-y-3"
      data-testid={`stair-props-${room.id}`}
    >
      <div className="flex items-center gap-2">
        <Footprints className="w-4 h-4 text-[#D96C4A] flex-shrink-0" />
        <span className="text-xs uppercase tracking-[0.15em] font-bold text-[#2C2C2A]">
          Trap eigenschappen
        </span>
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="ml-auto w-7 h-7 rounded-md hover:bg-[#FBE9DF] active:bg-[#F4D6C4] flex items-center justify-center text-[#5D7259] hover:text-[#D96C4A]"
              aria-label="Info over autofill"
              data-testid={`stair-props-info-${room.id}`}
            >
              <Info className="w-4 h-4" />
            </button>
          </PopoverTrigger>
          <PopoverContent
            side="bottom" align="end"
            className="max-w-[260px] text-[12px] leading-snug p-3"
          >
            Aangevinkte trap-werken vullen automatisch hun hoeveelheid uit deze velden
            (per trede, lm leuning, m² overloop, …). Per regel kan je dat nadien manueel
            aanpassen.
          </PopoverContent>
        </Popover>
      </div>

      <div className="grid grid-cols-1 min-[480px]:grid-cols-2 sm:grid-cols-3 gap-3">
        {/* Type trap — volledige breedte */}
        <div className="col-span-1 min-[480px]:col-span-2 sm:col-span-3">
          <Label className="text-[11px] uppercase tracking-[0.1em] font-semibold text-[#2C2C2A]">
            Type trap
          </Label>
          <Select
            value={sp.stair_type}
            onValueChange={(v) => patch({ stair_type: v })}
          >
            <SelectTrigger
              className="h-12 mt-1 rounded-lg text-base font-semibold text-[#2C2C2A]"
              data-testid={`stair-type-${room.id}`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STAIR_TYPES.map((t) => (
                <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <StairField
          label="Aantal treden" unit="stuks"
          value={sp.tread_count}
          onChange={(v) => patch({ tread_count: Math.max(0, Math.round(+v || 0)) })}
          testId={`stair-treads-${room.id}`}
        />
        <StairField
          label="Trapleuning" unit="lm"
          value={sp.handrail_lm}
          onChange={(v) => patch({ handrail_lm: Math.max(0, +v || 0) })}
          testId={`stair-handrail-${room.id}`}
        />
        <StairField
          label="Balustrade" unit="lm"
          value={sp.balustrade_lm}
          onChange={(v) => patch({ balustrade_lm: Math.max(0, +v || 0) })}
          testId={`stair-balustrade-${room.id}`}
        />
        <StairField
          label="Spijlen" unit="stuks"
          value={sp.spindle_count}
          onChange={(v) => patch({ spindle_count: Math.max(0, Math.round(+v || 0)) })}
          testId={`stair-spindles-${room.id}`}
        />
        {/* Overloop m² — alleen tonen wanneer stair_type === 'with_landing' (F9.3 item 3). */}
        {sp.stair_type === "with_landing" && (
          <StairField
            label="Overloop" unit="m²"
            value={sp.landing_m2}
            onChange={(v) => patch({ landing_m2: Math.max(0, +v || 0) })}
            testId={`stair-landing-${room.id}`}
          />
        )}
        <StairField
          label="Onderzijde trap" unit="m²"
          value={sp.underside_m2}
          onChange={(v) => patch({ underside_m2: Math.max(0, +v || 0) })}
          testId={`stair-underside-${room.id}`}
        />
      </div>
    </div>
  );
}

// Een veld: donker label + (eenheid) in subtiel grijs + groot/vet getal.
function StairField({ label, unit, value, onChange, testId }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-[0.1em] font-semibold text-[#2C2C2A]">
        {label}
        <span className="ml-1 font-normal normal-case text-[#5D7259]">({unit})</span>
      </Label>
      <RawNumInput
        value={value}
        onChange={onChange}
        placeholder="0"
        className="h-12 mt-1 rounded-lg text-right font-mono text-lg font-bold text-[#2C2C2A]"
        data-testid={testId}
      />
    </div>
  );
}
